import { AfterViewInit, Directive, ElementRef, Input, OnDestroy, Renderer2 } from '@angular/core';

@Directive({
    selector: '[monFoldable]',
    standalone: true,
})
export class MonFoldableDirective implements AfterViewInit, OnDestroy {
    @Input() showMoreLabel: string | undefined;
    @Input() showLessLabel: string | undefined;

    private expanded = false;
    private buttonEl: HTMLElement | null = null;
    private mutationObserver: MutationObserver | null = null;
    private textWrapper: HTMLElement | null = null;
    private buttonClickDisposer: (() => void) | null = null;

    constructor (private el: ElementRef<HTMLElement>, private renderer: Renderer2) {}

    ngAfterViewInit (): void {
        const host = this.el.nativeElement;
        this.wrapExistingContent();
        this.setupHostLayout(host);
        this.applyCollapsedClasses();
        this.createButtonIfOverflow();
        this.mutationObserver = new MutationObserver(() => this.updateDynamicState());
        this.mutationObserver.observe(host, { childList: true, subtree: true, characterData: true });
    }

    private wrapExistingContent (): void {
        const host = this.el.nativeElement;
        // Create wrapper span to hold original content (excluding any existing button).
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        const wrapper = this.renderer.createElement('span');
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        this.textWrapper = wrapper;
        this.renderer.addClass(wrapper, 'tw-inline-block');
        this.renderer.setStyle(wrapper, 'min-width', '0');
        this.renderer.setStyle(wrapper, 'flex', '1 1 auto');

        const children: Node[] = Array.from(host.childNodes);
        children.forEach(node => {
            this.renderer.appendChild(wrapper, node);
        });
        this.renderer.appendChild(host, wrapper);
    }

    private setupHostLayout (host: HTMLElement): void {
        const currentDisplay = getComputedStyle(host).display;
        const inline = currentDisplay === 'inline' || currentDisplay === 'inline-block';
        this.renderer.setStyle(host, 'display', inline ? 'inline-flex' : 'flex');
        this.renderer.setStyle(host, 'flex-direction', 'row');
        this.renderer.setStyle(host, 'align-items', 'center');
        this.renderer.setStyle(host, 'max-width', '100%');
    }

    private applyCollapsedClasses (): void {
        if (!this.textWrapper) {
            return;
        }
        this.textWrapper.classList.add('tw-overflow-hidden', 'tw-text-ellipsis', 'tw-whitespace-nowrap');
    }

    private removeCollapsedClasses (): void {
        if (!this.textWrapper) {
            return;
        }
        this.textWrapper.classList.remove('tw-text-ellipsis', 'tw-whitespace-nowrap', 'tw-overflow-hidden');
        this.textWrapper.classList.add('tw-break-words');
    }

    private toggle (): void {
        const host = this.el.nativeElement;
        this.expanded = !this.expanded;
        if (this.expanded) {
            this.removeCollapsedClasses();
            // Expanded: stack content then button below (column).
            this.renderer.setStyle(host, 'display', 'flex');
            this.renderer.setStyle(host, 'flex-direction', 'column');
            this.renderer.setStyle(host, 'align-items', 'flex-start');
            if (this.textWrapper) {
                this.renderer.setStyle(this.textWrapper, 'display', 'block');
                // Allow wrapping and word breaks to avoid horizontal overflow.
                this.textWrapper.classList.add('tw-break-words');
                this.renderer.setStyle(this.textWrapper, 'white-space', 'normal');
                this.renderer.setStyle(this.textWrapper, 'overflow-wrap', 'anywhere');
                this.renderer.setStyle(this.textWrapper, 'word-break', 'break-word');
                this.renderer.removeClass(this.textWrapper, 'tw-inline-block');
                this.renderer.removeStyle(this.textWrapper, 'flex');
                this.renderer.removeStyle(this.textWrapper, 'min-width');
            }
            if (this.buttonEl) {
                this.buttonEl.innerText = this.showLessLabel || 'Show less';
                this.renderer.setAttribute(this.buttonEl, 'aria-expanded', 'true');
            }
        } else {
            this.applyCollapsedClasses();
            this.createButtonIfOverflow();
            // Collapsed: single row.
            this.renderer.setStyle(host, 'flex-direction', 'row');
            this.renderer.setStyle(host, 'align-items', 'center');
            // Remove expanded-specific styles.
            this.renderer.removeStyle(host, 'width');
            this.renderer.removeStyle(host, 'overflow');
            if (this.textWrapper) {
                this.renderer.setStyle(this.textWrapper, 'display', 'inline-block');
                this.textWrapper.classList.remove('tw-break-words');
                this.renderer.setStyle(this.textWrapper, 'flex', '1 1 auto');
                this.renderer.setStyle(this.textWrapper, 'min-width', '0');
                this.renderer.removeStyle(this.textWrapper, 'white-space');
                this.renderer.removeStyle(this.textWrapper, 'overflow-wrap');
                this.renderer.removeStyle(this.textWrapper, 'word-break');
            }
            if (this.buttonEl) {
                this.buttonEl.innerText = this.showMoreLabel || 'Show more';
                this.renderer.setAttribute(this.buttonEl, 'aria-expanded', 'false');
            }
        }
    }

    private checkOverflow (): boolean {
        if (!this.textWrapper) {
            return false;
        }
        // Measure wrapper only (exclude button width).
        return this.textWrapper.scrollWidth > this.textWrapper.clientWidth + 1;
    }

    private createButtonIfOverflow (): void {
        const host = this.el.nativeElement;
        if (!this.checkOverflow()) {
            // No overflow; remove button if exists.
            if (this.buttonEl) {
                this.renderer.removeChild(host, this.buttonEl);
                this.buttonEl = null;
            }
            return;
        }

        if (!this.buttonEl) {
            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
            const btn = this.renderer.createElement('button');
            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
            this.buttonEl = btn;
            // PrimeNG small text style classes (assuming global styles present)
            this.renderer.addClass(btn, 'p-button');
            this.renderer.addClass(btn, 'p-button-text');
            this.renderer.addClass(btn, 'p-button-sm');
            this.renderer.addClass(btn, 'tw-min-w-fit');
            // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
            btn.type = 'button';
            // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
            btn.innerText = this.showMoreLabel || 'Show more';
            this.renderer.setAttribute(btn, 'aria-expanded', 'false');
            this.renderer.setAttribute(btn, 'aria-label', 'Toggle expanded content');
            this.buttonClickDisposer = this.renderer.listen(btn, 'click', () => this.toggle());
            this.renderer.appendChild(host, btn);
        }
    }

    private updateDynamicState (): void {
        if (this.expanded) {
            return;
        } // Only care about collapsed state.
        this.createButtonIfOverflow();
    }

    ngOnDestroy (): void {
        if (this.mutationObserver) {
            this.mutationObserver.disconnect();
        }
        if (this.buttonClickDisposer) {
            this.buttonClickDisposer();
        }
    }
}
